/**
 * Image Optimization Utilities
 * Handles caching, compression hints, and format optimization for team logos and UI images.
 */

// Browser-side image cache using Map with TTL
const imageCache = new Map<string, { blob: string; timestamp: number }>();
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_SIZE = 200; // Max cached images

/**
 * Check if browser supports WebP format
 */
let webpSupported: boolean | null = null;

export async function supportsWebP(): Promise<boolean> {
  if (webpSupported !== null) return webpSupported;
  
  if (typeof window === 'undefined') {
    webpSupported = false;
    return false;
  }

  try {
    const testImage = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
    const img = new Image();
    
    webpSupported = await new Promise((resolve) => {
      img.onload = () => resolve(img.width > 0 && img.height > 0);
      img.onerror = () => resolve(false);
      img.src = testImage;
    });
  } catch {
    webpSupported = false;
  }
  
  return webpSupported;
}

/**
 * Get optimized image URL with caching parameters
 * Adds cache-busting for stale images while maintaining browser cache efficiency
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
  } = {}
): string | null {
  if (!url) return null;
  
  // Validate URL
  try {
    new URL(url);
  } catch {
    return null;
  }
  
  // API-Football images already optimized, just return
  if (url.includes('media.api-sports.io')) {
    return url;
  }
  
  return url;
}

/**
 * Preload critical images (above-the-fold logos)
 * Only adds preloads, cleaned up by cleanupImagePreloads when navigating away
 */
export function preloadImage(url: string, priority: 'high' | 'low' = 'low'): void {
  if (!url || typeof document === 'undefined') return;
  
  // Check if already preloaded
  const existing = document.querySelector(`link[rel="preload"][href="${url}"]`);
  if (existing) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  link.fetchPriority = priority;
  link.dataset.dynamicPreload = 'true'; // Mark for cleanup
  document.head.appendChild(link);
}

/**
 * Clean up dynamically added image preloads
 * Call this when navigating away from pages that added preloads
 */
export function cleanupImagePreloads(): void {
  if (typeof document === 'undefined') return;
  
  const dynamicPreloads = document.querySelectorAll('link[rel="preload"][data-dynamic-preload="true"]');
  dynamicPreloads.forEach(link => link.remove());
}

/**
 * Preload multiple images in batch
 */
export function preloadImages(urls: (string | null | undefined)[], priority: 'high' | 'low' = 'low'): void {
  const validUrls = urls.filter((url): url is string => !!url);
  
  // Limit preloading to first 10 to avoid overwhelming
  validUrls.slice(0, 10).forEach(url => preloadImage(url, priority));
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(
  baseUrl: string,
  sizes: number[] = [32, 44, 64, 88]
): string {
  // API-Football doesn't support dynamic resizing
  // Return single src for now
  return baseUrl;
}

/**
 * Get placeholder data URL for image loading state
 */
export function getPlaceholderDataUrl(width: number, height: number): string {
  // Tiny transparent SVG placeholder
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"><rect fill="transparent" width="${width}" height="${height}"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Get fallback initials SVG for missing logos
 */
export function getInitialsFallbackUrl(name: string, size: number = 44): string {
  const initials = name.substring(0, 2).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">
    <rect fill="hsl(217 32% 17%)" width="${size}" height="${size}" rx="${size / 2}"/>
    <text x="50%" y="50%" dy="0.35em" fill="hsl(210 40% 96%)" font-family="system-ui" font-size="${size * 0.35}" font-weight="600" text-anchor="middle">${initials}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Clean up old cached images
 */
export function cleanupImageCache(): void {
  const now = Date.now();
  const entries = Array.from(imageCache.entries());
  
  // Remove expired entries
  entries.forEach(([key, value]) => {
    if (now - value.timestamp > CACHE_TTL) {
      imageCache.delete(key);
    }
  });
  
  // If still too large, remove oldest entries
  if (imageCache.size > MAX_CACHE_SIZE) {
    const sortedEntries = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = sortedEntries.slice(0, imageCache.size - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => imageCache.delete(key));
  }
}

// Run cleanup periodically
if (typeof window !== 'undefined') {
  setInterval(cleanupImageCache, 5 * 60 * 1000); // Every 5 minutes
}
